// parties.js
//
// The parties side of api2, plus the small helpers every party surface needs:
// colors, label-to-key resolution, website cleanup, money and name formatting.
//
// Requests go through utils/api2, which caches by URL, so the ballot strip on
// the home panel, the same strip on the candidates page and the party page
// itself share one request per state rather than three.

// The request helper and link helpers live in their own modules now that
// spending and members' records use them too. Re-exported here so every
// existing import from utils/parties keeps working.
import { fetchApi2 } from './api2';

export { API2_BASE, Api2Error, fetchApi2 } from './api2';
export { openExternal, onActivate } from './external';

// ─── Endpoints ────────────────────────────────────────────────────────────────

export const fetchParty = (key) => fetchApi2(`/parties/${encodeURIComponent(key)}`);

export const fetchPartyMoney = (key) => fetchApi2(`/parties/${encodeURIComponent(key)}/money`);

export const fetchPartyNews = (key, { state, nationalOnly, limit = 6 } = {}) =>
  fetchApi2(`/parties/${encodeURIComponent(key)}/news`, {
    state,
    national_only: nationalOnly ? 'true' : undefined,
    limit,
  });

// The state party is the one committee role=state returns. The API already
// picks the largest where several qualify, so the first row is the answer.
export const fetchStateParty = (key, state) =>
  fetchApi2(`/parties/${encodeURIComponent(key)}/affiliates`, { state, role: 'state' }).then(
    (body) => {
      const rows = body?.committees || body?.results || body?.affiliates || [];
      return rows[0] || null;
    }
  );

export const fetchStanceComparison = (topic) => fetchApi2('/parties/stances', { topic });

// The next election in a state and who is on its ballot, in two calls: the
// ballot needs the date, and the date comes from /elections/dates.
//
// The ballot is used rather than /parties/{key}/candidates for "who is
// running" because it applies the certified-list rule: where a state publishes
// its candidate list, FEC filers who are not on it (primary losers,
// withdrawals) are set aside under not_on_candidate_list instead of being
// shown as candidates. The party endpoint counts every filer under the label.
//
// An empty answer is a real answer here -- a state between cycles can have no
// dated ballot yet -- so it resolves to empty lists rather than throwing.
export const fetchBallotParties = async (state) => {
  const dates = await fetchApi2('/elections/dates', { state, limit: 1 });
  const date = dates?.next?.election_date;
  if (!date) return { date: null, label: null, parties: [], candidates: [], truncated: false };

  const ballot = await fetchApi2('/elections/ballot', { state, date });
  return {
    date,
    label: ballot?.label || null,
    parties: Array.isArray(ballot?.parties_on_ballot) ? ballot.parties_on_ballot : [],
    candidates: Array.isArray(ballot?.candidates) ? ballot.candidates : [],
    truncated: Boolean(ballot?.truncated),
  };
};

// ─── Parties ──────────────────────────────────────────────────────────────────

export const PARTY_KEYS = [
  'democratic',
  'republican',
  'libertarian',
  'green',
  'constitution',
  'forward',
  'reform',
];

// The conventional color for each, used only as an accent: a dot, a band, a
// bar. Never as the only carrier of meaning -- every use sits beside a name.
const PARTY_META = {
  democratic: { color: '#2f6bd6', name: 'Democratic Party', short: 'Democratic' },
  republican: { color: '#d4383b', name: 'Republican Party', short: 'Republican' },
  libertarian: { color: '#e0b100', name: 'Libertarian Party', short: 'Libertarian' },
  green: { color: '#2f9a50', name: 'Green Party', short: 'Green' },
  constitution: { color: '#8b5a2b', name: 'Constitution Party', short: 'Constitution' },
  forward: { color: '#7a5cf0', name: 'Forward Party', short: 'Forward' },
  reform: { color: '#c46a1b', name: 'Reform Party', short: 'Reform' },
};

export const NO_PARTY_COLOR = '#8e8e93';

export const partyColor = (key) => PARTY_META[key]?.color || NO_PARTY_COLOR;
export const partyName = (key) => PARTY_META[key]?.name || key;
export const partyShortName = (key) => PARTY_META[key]?.short || key;

// For rows that arrive without a party_key -- local filings from api2's
// candidate endpoints carry only the label as the state printed it. The ballot
// endpoint never guesses a key, and neither does this: a label resolves only
// when it names the party outright. "Independent", "Nonpartisan", "Write-In",
// "American Independent" and every label not listed stay unlinked.
const LABEL_PATTERNS = [
  [/^(democrat|democratic)(\s+party)?$/i, 'democratic'],
  [/^dem$/i, 'democratic'],
  [/^democratic[-\s]farmer[-\s]labor$/i, 'democratic'],
  [/^dfl$/i, 'democratic'],
  [/^republican(\s+party)?$/i, 'republican'],
  [/^(rep|gop)$/i, 'republican'],
  [/^libertarian(\s+party)?$/i, 'libertarian'],
  [/^lib$/i, 'libertarian'],
  [/^green(\s+party)?$/i, 'green'],
  [/^constitution(\s+party)?$/i, 'constitution'],
  [/^forward(\s+party)?$/i, 'forward'],
  [/^reform(\s+party)?$/i, 'reform'],
];

export const partyKeyFromLabel = (label) => {
  if (!label || typeof label !== 'string') return null;
  const trimmed = label.trim();
  const match = LABEL_PATTERNS.find(([pattern]) => pattern.test(trimmed));
  return match ? match[1] : null;
};

export const partyPath = (key, stateAbbr) =>
  stateAbbr ? `/us/party/${key}?state=${stateAbbr}` : `/us/party/${key}`;

// ─── Formatting ───────────────────────────────────────────────────────────────

// Websites come from FEC Form 1 exactly as typed: "DEMOCRATS.ORG",
// "WWW.GOP.COM", "HTTP:/WWW.DFL.ORG". Read as the address each one names.
export const normalizeWebsite = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const bare = raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/*/, '');
  if (!bare || !bare.includes('.')) return null;
  const host = bare.replace(/^www\./, '').replace(/\/.*$/, '');
  return { url: `https://${bare}`, host };
};

const trimZero = (value) => value.toFixed(1).replace(/\.0$/, '');

export const formatMoney = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return null;
  const n = Number(amount);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${trimZero(n / 1e9)}b`;
  if (abs >= 1e6) return `$${trimZero(n / 1e6)}m`;
  if (abs >= 1e4) return `$${Math.round(n / 1e3)}k`;
  if (abs >= 1e3) return `$${trimZero(n / 1e3)}k`;
  return `$${Math.round(n)}`;
};

export const formatShare = (share) =>
  share === null || share === undefined ? null : `${Math.round(Number(share) * 100)}%`;

// Election dates are calendar days, not instants. new Date('2026-11-03') is
// midnight UTC, which is the evening of November 2 anywhere west of London.
export const formatElectionDay = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
};

export const formatNewsDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const SMALL_WORDS = new Set(['of', 'and', 'the', 'for', 'in', 'on', 'a', 'an', 'to']);
const KEEP_UPPER = new Set(['PAC', 'DC', 'USA', 'US', 'GOP', 'DFL', 'II', 'III', 'IV']);

// FEC committee names are filed in capitals. Title case reads as a name
// rather than a shout; acronyms stay as filed.
export const titleCaseFiling = (value) => {
  if (!value || typeof value !== 'string') return '';
  if (value !== value.toUpperCase()) return value;
  return value
    .split(/\s+/)
    .map((word, index) => {
      if (KEEP_UPPER.has(word.replace(/[^A-Z]/g, ''))) return word;
      const lower = word.toLowerCase();
      if (index > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

// FEC filers are "Last, First Middle". Flipped for display; a name with no
// comma is already in reading order.
export const displayFecName = (value) => {
  if (!value || typeof value !== 'string') return '';
  const cased = titleCaseFiling(value);
  const comma = cased.indexOf(',');
  if (comma === -1) return cased;
  const last = cased.slice(0, comma).trim();
  const rest = cased.slice(comma + 1).trim();
  return rest ? `${rest} ${last}` : last;
};

export const officeLabel = (office, district, t) => {
  if (office === 'US_Senate') return t('common:usSenate', 'U.S. Senate');
  if (office === 'US_President') return t('common:president', 'President');
  if (office === 'US_House') {
    const house = t('common:usHouse', 'U.S. House');
    if (!district || district === '00') return `${house}, ${t('common:atLarge', 'at-large')}`;
    return `${house}, ${t('common:districtLabel', 'District {{district}}', {
      district: String(Number(district)),
    })}`;
  }
  return office || '';
};